import { API } from "@swivl-mono/api-helper";
import { BrainResultInterface, From, MessageType } from "@swivl-mono/types";
import { Button, ButtonColor, FiltersDropdown, FiltersInterface, SearchInput, Spinner } from "@swivl-mono/ui";
import { DateTime } from "luxon";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Navigate, useParams } from "react-router-dom";

import { DateRangeSelect } from "../../../Components/BotAnalyticsDashboard/DateRangeSelect/DateRangeSelect";
import { Member } from "../../../Models/Member/Member.Current";

// Helper functions to extract data from events (moved outside component)
const getBrain = (events: any[]) => {
  const brainLoaded = events?.find((e) => e.type === "BRAIN_LOADED")?.data;
  return brainLoaded ? { name: brainLoaded.name, slug: brainLoaded.slug } : null;
};
const getIntent = (events: any[]) => {
  const intentTriggered = events?.find((e) => e.type === "INTENT_TRIGGERED");
  if (intentTriggered) return intentTriggered.data?.action?.intent || "-";
  const unhandledTriggered = events?.find((e) => e.type === "UNHANDLED_TRIGGERED");
  return unhandledTriggered ? "Default Unhandled" : "-";
};
const getQuest = (events: any[]) => events?.find((e) => e.type === "SENT_TO_QUEST")?.data?.quest;
const getEntities = (events: any[]) => {
  const entitiesEvent = events?.find((e) => e.type === "ENTITIES_SAVED");
  if (!entitiesEvent?.data?.entities) return "-";
  return Object.entries(entitiesEvent.data.entities)
    .map(([type, data]: [string, any]) => `${type}: ${data.value}`)
    .join(", ");
};

const extractBotResponseFromResult = async (orgId: number, result: BrainResultInterface): Promise<string> => {
  try {
    const allMessages = await API.Messages.getAllMessages(orgId, result.userId);
    const brainTimestamp = result.updatedAt ? new Date(result.updatedAt).getTime() : 0;
    const relevantMessages = allMessages
      .filter((msg) => msg.createdAt && new Date(msg.createdAt).getTime() >= brainTimestamp)
      .sort((a, b) => new Date(a.createdAt!).getTime() - new Date(b.createdAt!).getTime());

    // Find the first bot response (Text or Buttons) after the user's query
    const firstBotResponse = relevantMessages.find(
      (msg) => msg.from === From.BOT && (msg.type === MessageType.Text || msg.type === MessageType.Buttons)
    );

    if (!firstBotResponse) return "-";

    if (firstBotResponse.type === MessageType.Text && firstBotResponse.payload?.text) {
      return firstBotResponse.payload.text;
    }

    if (firstBotResponse.type === MessageType.Buttons && firstBotResponse.payload?.text) {
      let responseText = firstBotResponse.payload.text;

      if (firstBotResponse.payload.actions && Array.isArray(firstBotResponse.payload.actions)) {
        const buttonOptions = firstBotResponse.payload.actions
          .map((action) => action.text)
          .filter((text) => text)
          .join(" | ");
        if (buttonOptions) {
          responseText += ` ${buttonOptions}`;
        }
      }
      return responseText;
    }

    return "-";
  } catch (error) {
    console.error(`Failed to fetch bot response for user ${result.userId}:`, error);
    return "-";
  }
};

export function InteractionsSettingsView() {
  const { startDate: startDateParam, endDate: endDateParam } = useParams<{ startDate: string; endDate: string }>();
  const [brainResults, setBrainResults] = useState<BrainResultInterface[]>([]);
  const [allResultsForFilters, setAllResultsForFilters] = useState<BrainResultInterface[]>([]);
  const [botResponses, setBotResponses] = useState<{ [resultId: string]: string }>({});
  const [loadingResponses, setLoadingResponses] = useState(false);
  const [exportProgress, setExportProgress] = useState<{ isExporting: boolean; current: number; total: number }>({
    isExporting: false,
    current: 0,
    total: 0
  });
  const [totalCount, setTotalCount] = useState(0);
  const [initialLoading, setInitialLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchAllDates, setSearchAllDates] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [filters, setFilters] = useState<FiltersInterface[]>([]);
  const [appliedFilters, setAppliedFilters] = useState<FiltersInterface[]>([]);
  const startDate = useMemo(() => {
    return startDateParam ? DateTime.fromISO(startDateParam).toJSDate() : new Date();
  }, [startDateParam]);
  const endDate = useMemo(() => {
    return endDateParam ? DateTime.fromISO(endDateParam).toJSDate() : new Date();
  }, [endDateParam]);

  const hasActiveFilters = useMemo(() => appliedFilters.some((f) => f.filters.some((x) => x.on)), [appliedFilters]);
  const useLocalFilter = useMemo(() => searchQuery.trim() || hasActiveFilters, [searchQuery, hasActiveFilters]);

  // Load all results for building intent filter list (runs once per date range)
  useEffect(() => {
    if (!Member.orgId || !startDateParam || !endDateParam) return;
    API.Brains.getBrainResults(Member.orgId, {
      startDate: startDateParam,
      endDate: endDateParam,
      excludeDeleted: true,
      limit: 10000,
      offset: 0
    })
      .then((data) => {
        setAllResultsForFilters(data.results || []);
      })
      .catch((err) => {
        console.error("Failed to load all results for filters:", err);
      });
  }, [startDateParam, endDateParam]);

  // Load brain results - either by date range or search all dates
  useEffect(() => {
    if (!Member.orgId || !startDateParam || !endDateParam) return;
    if (useLocalFilter && !searchAllDates && allResultsForFilters.length > 0) {
      setBrainResults(allResultsForFilters);
      setTotalCount(allResultsForFilters.length);
      setInitialLoading(false);
      return;
    }

    const offset = (currentPage - 1) * pageSize;
    const limit = searchAllDates && searchQuery.trim() ? 10000 : pageSize;
    const requestOffset = searchAllDates && searchQuery.trim() ? 0 : offset;

    const handleResponse = (data: any) => {
      setBrainResults(data.results || []);
      setTotalCount(data.total);
      setInitialLoading(false);
    };

    const handleError = (err: any) => {
      console.error("Failed to load brain results:", err);
      setInitialLoading(false);
    };

    const apiCall =
      searchAllDates && searchQuery.trim()
        ? API.Brains.searchBrainResults(Member.orgId, searchQuery, { excludeDeleted: true, limit, offset: requestOffset })
        : API.Brains.getBrainResults(Member.orgId, {
            startDate: startDateParam,
            endDate: endDateParam,
            excludeDeleted: true,
            limit,
            offset: requestOffset
          });

    apiCall.then(handleResponse).catch(handleError);
  }, [startDateParam, endDateParam, searchAllDates, searchQuery, currentPage, pageSize, useLocalFilter, allResultsForFilters]);

  // Handle date changes
  const handleDatePicked = useCallback(
    (isStart: boolean, date?: Date) => {
      if (!date) return;
      const newStart = isStart ? DateTime.fromJSDate(date).toISODate() : startDateParam;
      const newEnd = isStart ? endDateParam : DateTime.fromJSDate(date).toISODate();
      window.location.href = `/settings/interactions/${newStart}/${newEnd}`;
    },
    [startDateParam, endDateParam]
  );

  // Reset filters to build intent list from all results in date range
  const resetFilters = useCallback(() => {
    // Use allResultsForFilters (all records in date range) to build comprehensive intent list
    let resultsForIntents = allResultsForFilters;
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      resultsForIntents = allResultsForFilters.filter((result) => result.query?.toLowerCase().includes(query));
    }

    // Extract unique intents from filtered results
    const uniqueIntents = new Set<string>();
    resultsForIntents.forEach((result) => {
      const intent = getIntent(result.events);
      uniqueIntents.add(intent);
    });

    setFilters([
      {
        id: "status",
        title: "Status",
        filters: [
          { id: "handled", name: "Handled", on: false },
          { id: "unhandled", name: "Unhandled", on: false }
        ]
      },
      {
        id: "intent",
        title: "Intent",
        filters: Array.from(uniqueIntents)
          .sort()
          .map((intent) => ({
            id: intent,
            name: intent,
            on: false
          }))
      }
    ]);
  }, [allResultsForFilters, searchQuery]);

  // Filter results by search query and filters
  const filteredResults = useMemo(() => {
    if (!useLocalFilter) return brainResults;

    let results = brainResults;

    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      results = results.filter((result) => result.query?.toLowerCase().includes(query));
    }

    const statusFilters = appliedFilters.find((f) => f.id === "status")?.filters.filter((f) => f.on) || [];
    if (statusFilters.length > 0) {
      results = results.filter((result) => {
        const isHandled = result.handled === true;
        const isUnhandled = result.handled === false || result.handled === undefined || result.handled === null;
        return statusFilters.some((f) => (f.id === "handled" && isHandled) || (f.id === "unhandled" && isUnhandled));
      });
    }

    const intentFilters = appliedFilters.find((f) => f.id === "intent")?.filters.filter((f) => f.on) || [];
    if (intentFilters.length > 0) {
      results = results.filter((result) => {
        const intent = getIntent(result.events);
        return intentFilters.some((f) => f.id === intent);
      });
    }

    return results;
  }, [brainResults, searchQuery, useLocalFilter, appliedFilters]);

  const paginatedResults = useMemo(() => {
    return useLocalFilter ? filteredResults.slice((currentPage - 1) * pageSize, currentPage * pageSize) : brainResults;
  }, [useLocalFilter, filteredResults, currentPage, pageSize, brainResults]);

  // Load bot responses for currently visible interactions
  useEffect(() => {
    if (!Member.orgId || paginatedResults.length === 0) return;

    const missingResponseResults = paginatedResults.filter((result) => !botResponses[result.id]);

    if (missingResponseResults.length === 0) {
      setLoadingResponses(false);
      return;
    }

    setLoadingResponses(true);

    const fetchBotResponses = async () => {
      const fetchPromises = missingResponseResults.map(async (result) => {
        const response = await extractBotResponseFromResult(Member.orgId, result);
        return {
          resultId: result.id,
          response
        };
      });

      try {
        const results = await Promise.all(fetchPromises);
        const responses: { [resultId: string]: string } = {};
        results.forEach(({ resultId, response }) => {
          responses[resultId] = response;
        });
        setBotResponses((prev) => ({ ...prev, ...responses }));
      } catch (error) {
        console.error("Failed to fetch bot responses:", error);
        const fallbackResponses: { [resultId: string]: string } = {};
        paginatedResults.forEach((result) => {
          fallbackResponses[result.id] = "-";
        });
        setBotResponses((prev) => ({ ...prev, ...fallbackResponses }));
      } finally {
        setLoadingResponses(false);
      }
    };
    fetchBotResponses();
  }, [paginatedResults, botResponses, Member.orgId]);

  // Reset state when search query or dates change
  useEffect(() => {
    setCurrentPage(1);
    setSearchAllDates(false);
  }, [searchQuery, startDateParam, endDateParam]);

  // Initialize filters when data loads or search changes (but not when filters themselves change)
  useEffect(() => {
    if (allResultsForFilters.length > 0 && !hasActiveFilters) {
      resetFilters();
    }
  }, [allResultsForFilters, searchQuery, hasActiveFilters, resetFilters]);

  const isSwivlStaff = Member.isSwivlStaff;

  // Handle redirect for missing date parameters
  if (!startDateParam || !endDateParam) {
    const end = DateTime.now().toISODate();
    const start = DateTime.now().minus({ days: 30 }).toISODate();
    return <Navigate to={`/settings/interactions/${start}/${end}`} replace />;
  }

  const totalPages = useLocalFilter ? Math.ceil(filteredResults.length / pageSize) : Math.ceil(totalCount / pageSize);

  const handleExport = async () => {
    if (!Member.orgId) return;

    const resultsToExport = useLocalFilter ? filteredResults : allResultsForFilters;
    if (resultsToExport.length === 0) return;

    setExportProgress({ isExporting: true, current: 0, total: resultsToExport.length });

    try {
      const exportBotResponses: { [resultId: string]: string } = {};

      for (let i = 0; i < resultsToExport.length; i++) {
        const result = resultsToExport[i];

        if (botResponses[result.id]) {
          exportBotResponses[result.id] = botResponses[result.id];
        } else {
          exportBotResponses[result.id] = await extractBotResponseFromResult(Member.orgId, result);
        }

        setExportProgress((prev) => ({ ...prev, current: i + 1 }));
      }

      // Generate CSV
      const escapeCSV = (field: string) =>
        field.includes(",") || field.includes('"') || field.includes("\n") ? `"${field.replace(/"/g, '""')}"` : field;
      const headers = isSwivlStaff
        ? ["Handled", "User Question", "Bot Response", "Conversation", "Brain", "Intent", "Quest", "Entities", "Updated"]
        : ["Handled", "User Question", "Bot Response", "Conversation", "Intent", "Updated"];

      const rows = resultsToExport.map((r) => {
        const base = [
          r.handled === true ? "Yes" : r.handled === false ? "No" : "-",
          escapeCSV(r.query || "-"),
          escapeCSV(exportBotResponses[r.id] || "-"),
          `${window.location.origin}/contact-center/conversations/user/${r.userId}`
        ];

        if (isSwivlStaff) {
          const brain = getBrain(r.events);
          const quest = getQuest(r.events);
          return [
            ...base,
            escapeCSV(brain?.name || "-"),
            escapeCSV(getIntent(r.events)),
            escapeCSV(quest?.name || "-"),
            escapeCSV(getEntities(r.events)),
            r.updatedAt ? new Date(r.updatedAt).toLocaleString("en-US") : "-"
          ];
        }
        return [...base, escapeCSV(getIntent(r.events)), r.updatedAt ? new Date(r.updatedAt).toLocaleString("en-US") : "-"];
      });

      const csv = [headers, ...rows].map((row) => row.join(",")).join("\n");
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = `interactions-${startDateParam}-to-${endDateParam}.csv`;
      link.click();
      URL.revokeObjectURL(link.href);
    } catch (error) {
      console.error("Export failed:", error);
      alert("Export failed. Please try again.");
    } finally {
      setExportProgress({ isExporting: false, current: 0, total: 0 });
    }
  };

  const handleClearSearch = () => {
    setSearchQuery("");
  };

  if (initialLoading) {
    return (
      <div className="flex flex-col overflow-y-auto p-5">
        <div style={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "400px" }}>
          <Spinner />
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col" style={{ height: "100vh", overflow: "hidden", padding: "20px" }}>
      <div style={{ marginBottom: "20px" }}>
        <div className="flex items-center justify-between gap-4">
          <DateRangeSelect startDate={startDate} endDate={endDate} datePicked={handleDatePicked} />
          <div className="flex items-center gap-2">
            <div>
              <FiltersDropdown
                filters={filters}
                resetFilters={() => {
                  resetFilters();
                  setAppliedFilters([]);
                  setCurrentPage(1);
                }}
                saveFilters={(newFilters) => {
                  setFilters(newFilters);
                  setAppliedFilters(newFilters);
                  setCurrentPage(1);
                }}
              />
            </div>
            <SearchInput
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onClear={handleClearSearch}
              placeholder="Search User Questions..."
            />
            <span className="body-sm whitespace-nowrap text-grey-600">
              {useLocalFilter ? `${filteredResults.length} of ${brainResults.length}` : totalCount} records{" "}
              {searchAllDates ? "(across all dates)" : ""}
            </span>
            <div style={{ marginTop: "0.5%" }}>
              <Button
                color={ButtonColor.BLACK}
                onClick={handleExport}
                disabled={(useLocalFilter ? filteredResults : brainResults).length === 0 || exportProgress.isExporting}
              >
                {exportProgress.isExporting ? `Exporting... (${exportProgress.current}/${exportProgress.total})` : "Export CSV"}
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div style={{ flex: 1, overflow: "auto" }}>
        <table
          style={{
            width: "100%",
            borderCollapse: "collapse",
            backgroundColor: "white",
            borderRadius: "8px",
            overflow: "hidden",
            boxShadow: "0 1px 3px rgba(0,0,0,0.1)"
          }}
        >
          <thead style={{ backgroundColor: "#f8f9fa", borderBottom: "2px solid #dee2e6" }}>
            <tr>
              <th style={{ padding: "8px 12px", textAlign: "left", fontWeight: 600, fontSize: "13px", width: "60px" }}>Handled</th>
              <th style={{ padding: "8px 12px", textAlign: "left", fontWeight: 600, fontSize: "13px" }}>User Question</th>
              <th style={{ padding: "8px 12px", textAlign: "left", fontWeight: 600, fontSize: "13px" }}>Bot Response</th>
              <th style={{ padding: "8px 12px", textAlign: "left", fontWeight: 600, fontSize: "13px", width: "50px" }}>Conversation</th>
              {isSwivlStaff && <th style={{ padding: "8px 12px", textAlign: "left", fontWeight: 600, fontSize: "13px", width: "120px" }}>Brain</th>}
              <th style={{ padding: "8px 12px", textAlign: "left", fontWeight: 600, fontSize: "13px", width: "140px" }}>Intent</th>
              {isSwivlStaff && <th style={{ padding: "8px 12px", textAlign: "left", fontWeight: 600, fontSize: "13px", width: "150px" }}>Quest</th>}
              {isSwivlStaff && (
                <th style={{ padding: "8px 12px", textAlign: "left", fontWeight: 600, fontSize: "13px", width: "120px" }}>Entities</th>
              )}
              <th style={{ padding: "8px 12px", textAlign: "left", fontWeight: 600, fontSize: "13px", width: "120px" }}>Updated</th>
            </tr>
          </thead>
          <tbody>
            {paginatedResults.length === 0 ? (
              <tr>
                <td colSpan={isSwivlStaff ? 9 : 6} style={{ textAlign: "center", padding: "40px", color: "#6c757d" }}>
                  {searchQuery.trim() && !searchAllDates ? (
                    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "12px" }}>
                      {hasActiveFilters ? (
                        <>
                          <div>No interactions found for "{searchQuery}" with current filters.</div>
                          <Button
                            color={ButtonColor.BLACK}
                            onClick={() => {
                              resetFilters();
                              setAppliedFilters([]);
                              setCurrentPage(1);
                            }}
                          >
                            Reset Filters
                          </Button>
                        </>
                      ) : (
                        <>
                          <div>No interactions found for "{searchQuery}" in the selected date range.</div>
                          <Button
                            color={ButtonColor.BLACK}
                            onClick={() => {
                              setSearchAllDates(true);
                              setFilters([]);
                              setAppliedFilters([]);
                            }}
                          >
                            Search across all dates
                          </Button>
                        </>
                      )}
                    </div>
                  ) : (
                    "No interactions found."
                  )}
                </td>
              </tr>
            ) : (
              paginatedResults.map((result) => (
                <tr key={result.id} style={{ borderBottom: "1px solid #e9ecef" }}>
                  <td style={{ padding: "8px 12px", textAlign: "center", width: "60px" }}>
                    {result.handled === true ? (
                      <img
                        src="https://swivl-cdn.s3-us-west-1.amazonaws.com/check-circle.svg"
                        alt="Handled"
                        style={{ width: "24px", height: "24px", display: "block", margin: "0 auto" }}
                      />
                    ) : result.handled === false ? (
                      <img
                        src="https://swivl-cdn.s3-us-west-1.amazonaws.com/exclamation-circle.svg"
                        alt="Unhandled"
                        style={{ width: "24px", height: "24px", display: "block", margin: "0 auto" }}
                      />
                    ) : (
                      "–"
                    )}
                  </td>
                  <td
                    style={{
                      padding: "8px 12px",
                      fontSize: "13px",
                      maxWidth: "300px",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap"
                    }}
                    title={result.query || "-"}
                  >
                    {result.query || "-"}
                  </td>
                  <td
                    style={{
                      padding: "8px 12px",
                      fontSize: "13px",
                      maxWidth: "300px",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap"
                    }}
                    title={botResponses[result.id] || "-"}
                  >
                    {loadingResponses ? <Spinner /> : botResponses[result.id] || "-"}
                  </td>
                  <td style={{ padding: "8px 12px", textAlign: "center", width: "50px" }}>
                    <a
                      href={`/contact-center/conversations/user/${result.userId}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ fontSize: "18px", textDecoration: "none" }}
                      title="View Conversation"
                    >
                      💬
                    </a>
                  </td>
                  {isSwivlStaff &&
                    (() => {
                      const brain = getBrain(result.events);
                      return (
                        <td
                          style={{
                            padding: "8px 12px",
                            maxWidth: "120px",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                            fontSize: "13px"
                          }}
                          title={brain?.name}
                        >
                          {brain ? (
                            brain.slug ? (
                              <a
                                href={`/brain/${brain.slug}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{ color: "#ff6b35", textDecoration: "none" }}
                              >
                                {brain.name}
                              </a>
                            ) : (
                              brain.name
                            )
                          ) : (
                            "-"
                          )}
                        </td>
                      );
                    })()}
                  <td
                    style={{
                      padding: "8px 12px",
                      maxWidth: "140px",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      fontSize: "13px"
                    }}
                    title={getIntent(result.events)}
                  >
                    {getIntent(result.events)}
                  </td>
                  {isSwivlStaff &&
                    (() => {
                      const quest = getQuest(result.events);
                      return (
                        <td
                          style={{
                            padding: "8px 12px",
                            maxWidth: "150px",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                            fontSize: "13px"
                          }}
                          title={quest?.name}
                        >
                          {quest ? (
                            <a
                              href={`/quest/${quest.slug}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              style={{ color: "#ff6b35", textDecoration: "none" }}
                            >
                              {quest.name}
                            </a>
                          ) : (
                            "-"
                          )}
                        </td>
                      );
                    })()}
                  {isSwivlStaff && (
                    <td
                      style={{
                        padding: "8px 12px",
                        maxWidth: "120px",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        fontSize: "13px"
                      }}
                      title={getEntities(result.events)}
                    >
                      {getEntities(result.events)}
                    </td>
                  )}
                  <td style={{ padding: "8px 12px", width: "120px", fontSize: "12px", lineHeight: "1.4" }}>
                    {result.updatedAt ? (
                      <>
                        <div>{new Date(result.updatedAt).toLocaleDateString()}</div>
                        <div>{new Date(result.updatedAt).toLocaleTimeString()}</div>
                      </>
                    ) : (
                      "-"
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>

        {totalPages > 1 && (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              marginTop: "20px",
              padding: "16px",
              backgroundColor: "white",
              borderRadius: "8px",
              boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
              position: "relative"
            }}
          >
            <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
              <Button color={ButtonColor.BLACK} onClick={() => setCurrentPage(1)} disabled={currentPage === 1}>
                First
              </Button>
              <Button color={ButtonColor.BLACK} onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))} disabled={currentPage === 1}>
                Previous
              </Button>
              <span style={{ margin: "0 12px", fontSize: "14px", color: "#495057" }}>
                Page {currentPage} of {totalPages}
              </span>
              <Button
                color={ButtonColor.BLACK}
                onClick={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}
                disabled={currentPage === totalPages}
              >
                Next
              </Button>
              <Button color={ButtonColor.BLACK} onClick={() => setCurrentPage(totalPages)} disabled={currentPage === totalPages}>
                Last
              </Button>
            </div>
            <div
              style={{ position: "absolute", right: "16px", display: "flex", alignItems: "center", gap: "8px", fontSize: "14px", color: "#495057" }}
            >
              <select
                value={pageSize}
                onChange={(e) => {
                  setPageSize(Number(e.target.value));
                  setCurrentPage(1);
                }}
                style={{ padding: "6px 12px", border: "1px solid #ced4da", borderRadius: "4px", backgroundColor: "white", cursor: "pointer" }}
              >
                <option value={25}>25</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
                <option value={200}>200</option>
              </select>
              <span> records per page</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
